import { CpmButton } from '../../../components/design-system';
import { REPORTS_COPY } from '../copy';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';
import { ReportsAlert, ReportsWait } from './ReportsDialog';

interface InfiniteScrollSentinelProps {
    hasMore: boolean;
    loading?: boolean;
    error?: boolean;
    disabled?: boolean;
    onLoadMore: () => void;
    pageSize?: number;
    showEnd?: boolean;
    endVariant?: 'plain' | 'rule';
    compact?: boolean;
    loadingLabel?: string;
}

export default function InfiniteScrollSentinel({
    hasMore,
    loading = false,
    error = false,
    disabled = false,
    onLoadMore,
    pageSize = 25,
    showEnd = false,
    endVariant = 'plain',
    compact = false,
    loadingLabel,
}: InfiniteScrollSentinelProps) {
    const sentinelRef = useInfiniteScroll({
        hasMore: hasMore && !error,
        loading,
        disabled,
        onLoadMore,
    });

    const pad = compact ? '8px 10px' : '12px 16px';

    if (error) {
        return (
            <div style={{ padding: pad, borderTop: '1px solid var(--cpm-border-default)' }}>
                <ReportsAlert
                    tone="danger"
                    title={`Couldn’t load the next ${pageSize}`}
                    body={REPORTS_COPY.MORE_FAILED_BODY}
                    action={
                        <CpmButton variant="outline" size="sm" onClick={onLoadMore}>
                            {`Retry the next ${pageSize}`}
                        </CpmButton>
                    }
                />
            </div>
        );
    }

    if (loading) {
        const skRows = compact ? 2 : 3;
        const skHeight = compact ? 36 : 56;
        return (
            <div style={{ borderTop: '1px solid var(--cpm-border-default)' }}>
                <div
                    className="flex items-center"
                    style={{ gap: 9, padding: pad }}
                >
                    <ReportsWait label={loadingLabel ?? `Getting the next ${pageSize}…`} />
                </div>
                {Array.from({ length: skRows }, (_, i) => (
                    <div
                        key={i}
                        className="cpm-sk"
                        style={{
                            height: skHeight,
                            borderRadius: 0,
                            borderTop: '1px solid var(--cpm-border-default)',
                        }}
                    />
                ))}
            </div>
        );
    }

    if (hasMore) {
        return (
            <div
                ref={sentinelRef}
                aria-hidden
                style={{ height: 1, width: '100%' }}
            />
        );
    }

    if (!showEnd) return null;

    if (endVariant === 'rule') {
        return (
            <div
                className="flex items-center justify-center"
                style={{ gap: 10, padding: compact ? '9px 10px 5px' : '12px 16px' }}
            >
                <span
                    style={{
                        height: 1,
                        flex: 1,
                        background: 'var(--cpm-border-default)',
                    }}
                />
                <span
                    className="uppercase"
                    style={{
                        fontSize: 9.5,
                        fontWeight: 800,
                        letterSpacing: '0.08em',
                        color: 'var(--cpm-text-tertiary)',
                    }}
                >
                    {REPORTS_COPY.END_OF_LIST}
                </span>
                <span
                    style={{
                        height: 1,
                        flex: 1,
                        background: 'var(--cpm-border-default)',
                    }}
                />
            </div>
        );
    }

    return (
        <p
            className="m-0 text-center uppercase"
            style={{
                padding: 10,
                fontSize: 10.5,
                fontWeight: 800,
                letterSpacing: '0.08em',
                color: 'var(--cpm-text-tertiary)',
            }}
        >
            {REPORTS_COPY.END_OF_LIST}
        </p>
    );
}
